import { API_URL } from '@/lib/api';
import type { Trail, TrailPoint } from '@/types/trail';
import { prepareLocalImage } from '@/utils/imageConversion';
import { getBestAvailableLocation } from '@/utils/location';
import { ensureForegroundLocationPermission } from '@/utils/permissions';
import * as Location from 'expo-location';

// ─── Trails API Functions ────────────────────────────────────────────
// Standalone fetch functions extracted from TrailsContext.

export interface TrailPage {
  trails: Trail[];
  nextOffset: number | null;
  total: number;
}

export interface TrailStats {
  totalWalks: number;
  totalTime: number;
  totalDistance: number;
}

const dedupeTrailsById = (items: Trail[]): Trail[] => {
  const seen = new Set<string>();
  return items.filter((trail) => {
    const id = String((trail as any)?.id ?? '');
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/** Normalize a backend page envelope (camelCase) into a TrailPage, deduping by id. */
export function toTrailPage(raw: any): TrailPage {
  const trails: Trail[] = Array.isArray(raw?.trails) ? raw.trails : Array.isArray(raw) ? raw : [];
  const deduped = dedupeTrailsById(trails);
  const nextOffset = typeof raw?.nextOffset === 'number' ? raw.nextOffset : null;
  // total is the full pre-pagination count from the backend (COUNT(*) OVER ()).
  // Fall back to the deduped page length when absent (older backends).
  const total = typeof raw?.total === 'number' ? raw.total : deduped.length;
  return { trails: deduped, nextOffset, total };
}

/** Append an optional `dog_id` query param to a URL. (snake_case to match the
 *  FastAPI `get_chosen_dog` dependency — sending `dogId` was silently dropped,
 *  so the backend always fell back to the user's first dog.) */
export function withDog(url: string, dogId?: string) {
  return dogId ? `${url}${url.includes('?') ? '&' : '?'}dog_id=${encodeURIComponent(dogId)}` : url;
}

export async function fetchMyTrails(token: string, userId: string, dogId?: string, offset = 0): Promise<TrailPage> {
  const hasPerm = await ensureForegroundLocationPermission();
  if (!hasPerm) {
    console.warn('Location permission not granted; skipping /trail/me fetch');
    return { trails: [], nextOffset: null, total: 0 };
  }

  const searchParams = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  try {
    const loc = await getBestAvailableLocation();
    if (loc?.coords) {
      searchParams.set('latitude', String(loc.coords.latitude));
      searchParams.set('longitude', String(loc.coords.longitude));
    }
  } catch (err) {
    console.warn('Failed to get location for /trail/me call:', err);
  }

  const resp = await fetch(withDog(`${API_URL}/trail/me?${searchParams.toString()}`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Failed to load my trails (${resp.status})`);
  return toTrailPage(await resp.json());
}

export async function fetchMyTrailsHistory(token: string, dogId?: string, offset = 0): Promise<TrailPage> {
  const hasPerm = await ensureForegroundLocationPermission();
  if (!hasPerm) return { trails: [], nextOffset: null, total: 0 };

  const searchParams = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  try {
    const loc = await getBestAvailableLocation({ accuracy: Location.Accuracy.Balanced });
    if (loc?.coords) {
      searchParams.set('latitude', String(loc.coords.latitude));
      searchParams.set('longitude', String(loc.coords.longitude));
    }
  } catch {
    // proceed without location
  }

  const resp = await fetch(withDog(`${API_URL}/trail/me/history?${searchParams.toString()}`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Failed to load trail history (${resp.status})`);
  return toTrailPage(await resp.json());
}

export async function fetchMyTrailsStats(token: string, dogId?: string): Promise<TrailStats> {
  const resp = await fetch(withDog(`${API_URL}/trail/me/stats`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Failed to load trail stats (${resp.status})`);
  const data = await resp.json();
  return {
    totalWalks: Number(data?.totalWalks ?? 0),
    totalTime: Number(data?.totalTime ?? 0),
    totalDistance: Number(data?.totalDistance ?? 0),
  };
}

export async function fetchSavedTrails(token: string, dogId?: string, offset = 0): Promise<TrailPage> {
  const hasPerm = await ensureForegroundLocationPermission();
  if (!hasPerm) {
    console.warn('Location permission not granted; skipping saved trails fetch');
    return { trails: [], nextOffset: null, total: 0 };
  }

  const searchParams = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  try {
    const loc = await getBestAvailableLocation();
    if (loc?.coords) {
      searchParams.set('latitude', String(loc.coords.latitude));
      searchParams.set('longitude', String(loc.coords.longitude));
    }
  } catch (err) {
    console.warn('Failed to get location for saved trails call:', err);
  }

  const resp = await fetch(withDog(`${API_URL}/trail/me/saved-trails?${searchParams.toString()}`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Failed to load saved trails (${resp.status})`);
  return toTrailPage(await resp.json());
}

export async function fetchTrailDetail(id: string, token?: string, dogId?: string): Promise<Trail | undefined> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const resp = await fetch(withDog(`${API_URL}/trail/${id}`, dogId), { headers });
  if (resp.status === 403) throw new Error('NO_ACCESS');
  if (!resp.ok) throw new Error(`Failed to fetch trail (${resp.status})`);
  const data = await resp.json();
  // Backend may return the per-fix time series as `trailPoints` (camelCase
  // from the API schema). Normalize the key if it came back snake_case, and
  // normalize recorded_at → recordedAt as ms epoch: the backend serializes
  // datetimes as ISO strings, but charts subtract them as numbers (string −
  // string = NaN → moving/sniff breakdown null → DonutChart never renders).
  const pts = data?.trailPoints ?? data?.trail_points;
  if (pts) {
    data.trailPoints = pts.map((p: any) => ({
      ...p,
      recordedAt: new Date(p.recordedAt ?? p.recorded_at).getTime(),
    }));
  }
  return data;
}

export const PAGE_SIZE = 20;

export async function fetchNearbyTrails(
  token: string,
  params: { userLat?: number; userLng?: number; mapLat?: number; mapLng?: number; distanceKm?: number },
  dogId?: string,
  offset = 0,
): Promise<TrailPage> {
  const effectiveMapLat = params.mapLat ?? params.userLat;
  const effectiveMapLng = params.mapLng ?? params.userLng;

  if (typeof effectiveMapLat !== 'number' || typeof effectiveMapLng !== 'number') return { trails: [], nextOffset: null, total: 0 };

  const searchParams = new URLSearchParams({
    latitude: String(effectiveMapLat),
    longitude: String(effectiveMapLng),
    distance_km: String(params.distanceKm ?? 35),
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  if (typeof params.userLat === 'number' && typeof params.userLng === 'number') {
    searchParams.set('user_latitude', String(params.userLat));
    searchParams.set('user_longitude', String(params.userLng));
  }

  const resp = await fetch(withDog(`${API_URL}/trail/nearby?${searchParams.toString()}`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 404) return { trails: [], nextOffset: null, total: 0 };
  if (!resp.ok) throw new Error(`Failed to load nearby trails (${resp.status})`);
  return toTrailPage(await resp.json());
}

export async function fetchForYouTrails(
  token: string,
  params: { userLat?: number; userLng?: number; mapLat?: number; mapLng?: number; distanceKm?: number },
  dogId?: string,
  offset = 0,
): Promise<TrailPage> {
  let uLat = params.userLat;
  let uLng = params.userLng;
  const effectiveMapLat = params.mapLat ?? params.userLat;
  const effectiveMapLng = params.mapLng ?? params.userLng;

  if (typeof uLat !== 'number' || typeof uLng !== 'number') {
    try {
      const loc = await getBestAvailableLocation();
      if (loc?.coords) {
        uLat = loc.coords.latitude;
        uLng = loc.coords.longitude;
      } else {
        return { trails: [], nextOffset: null, total: 0 };
      }
    } catch {
      return { trails: [], nextOffset: null, total: 0 };
    }
  }

  const searchParams = new URLSearchParams({
    latitude: String(effectiveMapLat ?? uLat),
    longitude: String(effectiveMapLng ?? uLng),
    distance_km: String(params.distanceKm ?? 35),
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  if (typeof uLat === 'number' && typeof uLng === 'number') {
    searchParams.set('user_latitude', String(uLat));
    searchParams.set('user_longitude', String(uLng));
  }

  const resp = await fetch(withDog(`${API_URL}/trail/for-you?${searchParams.toString()}`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 404) return { trails: [], nextOffset: null, total: 0 }; // no trails for this area
  if (!resp.ok) throw new Error(`/trail/for-you failed (${resp.status})`);
  return toTrailPage(await resp.json());
}

export async function fetchTrailsByTag(
  token: string,
  tag: string,
  params: { userLat?: number; userLng?: number; mapLat?: number; mapLng?: number; distanceKm?: number },
  dogId?: string,
  offset = 0,
): Promise<TrailPage> {
  let uLat = params.userLat;
  let uLng = params.userLng;
  const effectiveMapLat = params.mapLat ?? params.userLat;
  const effectiveMapLng = params.mapLng ?? params.userLng;

  if (typeof uLat !== 'number' || typeof uLng !== 'number') {
    try {
      const loc = await getBestAvailableLocation();
      if (loc?.coords) {
        uLat = loc.coords.latitude;
        uLng = loc.coords.longitude;
      } else {
        return { trails: [], nextOffset: null, total: 0 };
      }
    } catch {
      return { trails: [], nextOffset: null, total: 0 };
    }
  }

  const searchParams = new URLSearchParams({
    latitude: String(effectiveMapLat ?? uLat),
    longitude: String(effectiveMapLng ?? uLng),
    chosen_tag: tag,
    distance_km: String(params.distanceKm ?? 35),
    limit: String(PAGE_SIZE),
    offset: String(offset),
  });
  if (typeof uLat === 'number' && typeof uLng === 'number') {
    searchParams.set('user_latitude', String(uLat));
    searchParams.set('user_longitude', String(uLng));
  }

  const resp = await fetch(withDog(`${API_URL}/trail/tag?${searchParams.toString()}`, dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 404) return { trails: [], nextOffset: null, total: 0 }; // no trails for this tag/area
  if (!resp.ok) throw new Error(`/trail/tag failed (${resp.status})`);
  return toTrailPage(await resp.json());
}

interface SearchTrailParams {
  latitude: number;
  longitude: number;
  filterParams: Record<string, string | string[]>;
  distanceKm?: number;
  mapLatitude?: number;
  mapLongitude?: number;
  userLatitude?: number;
  userLongitude?: number;
  dogId?: string;
  offset?: number;
}

/** Shared URLSearchParams for /trail/search and /trail/search/count. */
function buildTrailSearchParams(params: SearchTrailParams): URLSearchParams {
  const effectiveMapLat = params.mapLatitude ?? params.latitude;
  const effectiveMapLng = params.mapLongitude ?? params.longitude;
  const effectiveUserLat = params.userLatitude ?? params.latitude;
  const effectiveUserLng = params.userLongitude ?? params.longitude;

  const searchParams = new URLSearchParams({
    latitude: String(effectiveMapLat),
    longitude: String(effectiveMapLng),
    distance_km: String(params.distanceKm ?? 35),
  });
  if (typeof effectiveUserLat === 'number' && typeof effectiveUserLng === 'number') {
    searchParams.set('user_latitude', String(effectiveUserLat));
    searchParams.set('user_longitude', String(effectiveUserLng));
  }

  for (const [key, value] of Object.entries(params.filterParams)) {
    if (key === 'tags' && Array.isArray(value)) {
      (value as string[]).forEach((tag) => searchParams.append('tags', tag));
    } else if (typeof value === 'string') {
      searchParams.set(key, value);
    }
  }
  return searchParams;
}

export async function countTrailsAPI(
  token: string,
  params: Omit<SearchTrailParams, 'offset'>
): Promise<number> {
  const searchParams = buildTrailSearchParams(params);
  const resp = await fetch(withDog(`${API_URL}/trail/search/count?${searchParams.toString()}`, params.dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`/trail/search/count failed (${resp.status})`);
  const data = await resp.json();
  return typeof data?.count === 'number' ? data.count : 0;
}

export async function searchTrailsAPI(
  token: string,
  params: SearchTrailParams
): Promise<TrailPage> {
  const searchParams = buildTrailSearchParams(params);
  searchParams.set('limit', String(PAGE_SIZE));
  searchParams.set('offset', String(params.offset ?? 0));

  const resp = await fetch(withDog(`${API_URL}/trail/search?${searchParams.toString()}`, params.dogId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 404) return { trails: [], nextOffset: null, total: 0 }; // no matching trails
  if (!resp.ok) throw new Error(`/trail/search failed (${resp.status})`);
  return toTrailPage(await resp.json());
}

export async function saveTrailAPI(token: string, userId: string, trail: Trail, opts?: { visibility?: import('@/types/group').Visibility; sharedGroupId?: string | null }): Promise<Trail> {
  const coordsToPath = (coords: { latitude: number; longitude: number }[] | undefined) => {
    if (!coords || coords.length === 0) return [] as number[][];
    return coords.map(c => [c.longitude, c.latitude]);
  };

  // Per-fix time series for backend smoothing. Backend prefers
  // `trailPoints` (rebuilds path via ST_MakeLine ORDER BY recorded_at)
  // and falls back to `path` when absent. `path` is kept during the
  // transition so existing ingest/spatial-search keep working.
  const trailPointsToPayload = (pts: TrailPoint[] | undefined) =>
    (pts ?? []).map(p => ({
      recorded_at: new Date(p.recordedAt).toISOString(),
      latitude: p.latitude,
      longitude: p.longitude,
      accuracy: p.accuracy,
      altitude: p.altitude,
      altitude_accuracy: p.altitudeAccuracy,
      speed: p.speed,
      heading: p.heading,
      odometer: p.odometer,
      activity_type: p.activityType,
      activity_confidence: p.activityConfidence,
      is_sample: p.isSample,
      is_mock: p.isMock,
      battery_level: p.batteryLevel,
      battery_charging: p.batteryCharging,
      platform: p.platform,
    }));

  const body: any = {
    name: trail.name,
    distance: trail.distance,
    duration: trail.duration,
    path: coordsToPath(trail.coordinates),
    trailPoints: trailPointsToPayload(trail.trailPoints),
    photo: trail.photo,
    photos: (trail as any).photos,
    urls: (trail as any).urls,
    city: trail.city,
    country: trail.country,
    description: trail.description,
    tags: trail.tags,
    elevation: trail.elevation,
    surface_type: trail.surfaceType,
    difficulty: trail.difficulty,
    location: trail.location,
    pace: trail.pace,
    speed: trail.speed,
    max_elevation: trail.maxElevation,
    rating: trail.rating,
    review: trail.review,
    environment_tags: trail.environment_tags,
    dogTraffic: (trail as any).dogTraffic,
    footTraffic: (trail as any).footTraffic,
    paths: (trail as any).paths,
    exposure: (trail as any).exposure,
    offLeash: (trail as any).offLeash,
    wildlife: (trail as any).wildlife,
    visibility: opts?.visibility,
    sharedGroupId: opts?.sharedGroupId ?? null,
  };

  const providedImages: any[] = [];
  if (Array.isArray((body as any).urls)) providedImages.push(...(body as any).urls);
  if (Array.isArray((body as any).photos)) providedImages.push(...(body as any).photos);

  const hostedUrls: string[] = [];
  const localFilesPromises: Promise<any>[] = [];
  for (const item of providedImages) {
    if (!item) continue;
    if (typeof item === 'string' && item.startsWith('http')) {
      hostedUrls.push(item);
    } else if (typeof item === 'string') {
      localFilesPromises.push(prepareLocalImage(item));
    } else if (typeof item === 'object' && item?.uri) {
      localFilesPromises.push(
        prepareLocalImage(item.uri).then((prepared) => ({ ...item, ...prepared })),
      );
    } else if (typeof item === 'object') {
      localFilesPromises.push(Promise.resolve(item));
    }
  }

  let resp: Response;
  const localFiles = await Promise.all(localFilesPromises);
  if (localFiles.length > 0) {
    // Two-phase create. The old multipart /trail/me/upload route can't carry
    // long recordings: the `trail` JSON part crosses Starlette's 1 MiB
    // multipart part cap at ~45 min of per-second fixes (400: "Part exceeded
    // maximum size of 1024KB"). Create the trail from JSON alone (no part
    // limit), then attach images with the same endpoint the edit flow uses.
    if (hostedUrls.length > 0) body.urls = hostedUrls;
    resp = await fetch(`${API_URL}/trail/me`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.text();
      throw new Error(`Failed to save trail: ${resp.status} ${err}`);
    }
    const created: Trail = await resp.json();
    try {
      const imgForm = new FormData();
      for (const f of localFiles) {
        imgForm.append('files', f as any);
      }
      const imgResp = await fetch(`${API_URL}/trail/${created.id}/images`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: imgForm as any,
      });
      if (imgResp.ok) return (await imgResp.json()) as Trail;
      // images failed but the trail is saved — don't lose the whole walk over it
      console.warn('Trail saved but image upload failed:', imgResp.status);
    } catch (e) {
      console.warn('Trail saved but image upload failed:', e);
    }
    return created;
  }
  if (hostedUrls.length > 0) body.urls = hostedUrls;
  resp = await fetch(`${API_URL}/trail/me`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Failed to save trail: ${resp.status} ${err}`);
  }
  return resp.json();
}

export async function saveFollowTrailAPI(
  token: string,
  userId: string,
  sourceTrailId: string,
  trail: Trail,
  review: { rating: number; content?: string },
  photos: string[] = [],
): Promise<Trail> {
  const coordsToPath = (coords: { latitude: number; longitude: number }[] | undefined) => {
    if (!coords || coords.length === 0) return [] as number[][];
    return coords.map(c => [c.longitude, c.latitude]);
  };

  const trailPointsToPayload = (pts: TrailPoint[] | undefined) =>
    (pts ?? []).map(p => ({
      recorded_at: new Date(p.recordedAt).toISOString(),
      latitude: p.latitude,
      longitude: p.longitude,
      accuracy: p.accuracy,
      altitude: p.altitude,
      altitude_accuracy: p.altitudeAccuracy,
      speed: p.speed,
      heading: p.heading,
      odometer: p.odometer,
      activity_type: p.activityType,
      activity_confidence: p.activityConfidence,
      is_sample: p.isSample,
      is_mock: p.isMock,
      battery_level: p.batteryLevel,
      battery_charging: p.batteryCharging,
      platform: p.platform,
    }));

  const trailData: any = {
    name: trail.name,
    description: trail.description,
    distance: trail.distance,
    duration: trail.duration,
    path: trail.path || coordsToPath(trail.coordinates),
    trailPoints: trailPointsToPayload(trail.trailPoints),
    tags: trail.tags,
    pace: trail.pace,
    speed: trail.speed,
    maxElevation: trail.maxElevation,
    dogTraffic: trail.dogTraffic,
    footTraffic: trail.footTraffic,
    paths: trail.paths,
    exposure: trail.exposure,
    offLeash: trail.offLeash,
    wildlife: trail.wildlife,
    isOriginal: false,
    originalTrailId: sourceTrailId,
    isPublic: true,
    rating: trail.rating,
    reviewCount: trail.reviewCount,
    startLatitude: trail.startLatitude ?? trail.coordinates?.[0]?.latitude,
    startLongitude: trail.startLongitude ?? trail.coordinates?.[0]?.longitude,
  };

  const hostedUrls: string[] = [];
  const localFilesPromises: Promise<any>[] = [];
  for (const item of (Array.isArray(photos) ? photos : [])) {
    if (!item) continue;
    if (typeof item === 'string' && item.startsWith('http')) {
      hostedUrls.push(item);
    } else if (typeof item === 'string') {
      localFilesPromises.push(prepareLocalImage(item));
    }
  }

  let resp: Response;
  const localFiles = await Promise.all(localFilesPromises);
  if (localFiles.length > 0) {
    // Two-phase create (same 1 MiB multipart part cap as saveTrailAPI —
    // long follow walks can't carry their trail JSON in a form part).
    resp = await fetch(`${API_URL}/trail/me/follow-trail/${sourceTrailId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ review, trail_data: { ...trailData, urls: hostedUrls } }),
    });
    if (!resp.ok) {
      const err = await resp.text().catch(() => '');
      throw new Error(err || 'Failed to save followed trail');
    }
    const created: Trail = await resp.json();
    try {
      const imgForm = new FormData();
      for (const file of localFiles) {
        imgForm.append('files', file as any);
      }
      const imgResp = await fetch(`${API_URL}/trail/${created.id}/images`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: imgForm as any,
      });
      if (imgResp.ok) return (await imgResp.json()) as Trail;
      console.warn('Followed trail saved but image upload failed:', imgResp.status);
    } catch (e) {
      console.warn('Followed trail saved but image upload failed:', e);
    }
    return created;
  }
  resp = await fetch(`${API_URL}/trail/me/follow-trail/${sourceTrailId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ review, trail_data: { ...trailData, urls: hostedUrls } }),
  });

  if (!resp.ok) {
    const err = await resp.text().catch(() => '');
    throw new Error(err || 'Failed to save followed trail');
  }

  return resp.json();
}

export async function deleteTrailAPI(token: string, id: string): Promise<void> {
  const resp = await fetch(`${API_URL}/trail/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status === 204) return;
  const body = await resp.text();
  throw new Error(`Failed to delete trail: ${resp.status} ${body}`);
}

export async function updateTrailNameAPI(token: string, id: string, name: string): Promise<Trail> {
  const resp = await fetch(`${API_URL}/trail/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name }),
  });
  if (!resp.ok) throw new Error(await resp.text() || 'Failed to update trail name');
  return resp.json();
}

export async function updateTrailPhotoAPI(token: string, id: string, photoOrUrls: string | string[]): Promise<Trail> {
  const uris = Array.isArray(photoOrUrls) ? photoOrUrls : [photoOrUrls];
  const prepared = await Promise.all(uris.filter(Boolean).map(prepareLocalImage));
  const form = new FormData();
  for (const p of prepared) {
    form.append('files', p as any);
  }

  const resp = await fetch(`${API_URL}/trail/${id}/images`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: form as any,
  });
  if (!resp.ok) throw new Error(await resp.text() || 'Failed to update image(s)');
  return resp.json();
}

export async function deleteTrailImageAPI(token: string, trailId: string, imageId: string): Promise<void> {
  const resp = await fetch(`${API_URL}/trail/${trailId}/images/${imageId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.status !== 204) throw new Error(await resp.text().catch(() => '') || 'Failed to delete image');
}

export async function updateTrailDetailsAPI(
  token: string,
  id: string,
  updates: {
    name?: string;
    description?: string;
    dogTraffic?: number;
    footTraffic?: number;
    paths?: number;
    exposure?: number;
    offLeash?: boolean;
    wildlife?: boolean;
    rating?: number;
  }
): Promise<Trail> {
  const resp = await fetch(`${API_URL}/trail/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(updates),
  });
  if (!resp.ok) throw new Error(await resp.text() || 'Failed to update details');
  return resp.json();
}

export async function saveTrailBookmarkAPI(token: string, trailId: string): Promise<any> {
  const resp = await fetch(`${API_URL}/trail/me/saved-trails/${encodeURIComponent(trailId)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Failed to save trail bookmark: ${resp.status} ${txt}`);
  }
  return resp.json();
}

export async function removeTrailBookmarkAPI(token: string, trailId: string): Promise<void> {
  const resp = await fetch(`${API_URL}/trail/me/saved-trails/${encodeURIComponent(trailId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`Failed to remove saved trail: ${resp.status} ${txt}`);
  }
}